import { Context, ExtraContext } from '@utilitywarehouse/tracking-core';
import { useEffect, useMemo } from 'react';

import { optlyEvents } from '@constants/trackingEvents';
import { useOptlyEvent } from '@features/Optimisely/useOptlyEvent';
import {
  Appointment,
  AttributionDetails,
  GetThankYouDataQuery,
  OccupancyType,
  ThankYouBasketFragment,
} from '@generated/gql/graphql';
import { useHandleCTAConversionParam } from '@hooks/useHandleCTAConversionParam';
import { useUserInformation } from '@hooks/useUserInformation';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useHostedAttribution } from '@providers/state/HostedAttributionProvider';
import { UserInformation } from '@src/types/auth';
import { useTrackingContext } from '@tracking/Provider/Context';
import {
  SignupApplicationEntity,
  type SignupCbcCalcStateEntity,
  signupApplicationEntity,
  signupCbcCalcStateEntity,
  signupCheckoutEntity,
  signupCoreServicesEntity,
  useTrackSignupCompletedOnce,
} from '@tracking/trackingEvents';
import { parseCoreServicesEntity } from '@tracking/utils';
import { buildOptimizelyEntity } from '@tracking/utils/buildOptimizelyEntity';
import { getNetworkType } from '@tracking/utils/getNetworkType';
import {
  parseAttributionContext,
  parseSessionContext,
} from '@tracking/utils/parseAttributionContext';
import { parseCheckoutEntity } from '@tracking/utils/parseCheckoutEntity';
import { formatInsurance } from '@utils/formatBasket/insurance';
import {
  completedBasketStorage,
  thankYouDetailsStorage,
} from '@utils/storagePropertyManagement';

const buildApplicationContext = (
  basket: ThankYouBasketFragment,
  attributionDetails: Nullable<AttributionDetails>,
  appointment: Nullable<Appointment>,
  userInformation: Nullable<UserInformation>,
  isHostedLink: boolean
): SignupApplicationEntity => {
  const sessionContext = parseSessionContext(appointment, userInformation);
  const attributionContext = parseAttributionContext(attributionDetails);
  const { boilerInsurance, incomeProtector } = formatInsurance(basket);
  const networkType = getNetworkType(basket);

  return {
    created_at: basket.details?.createdAt ?? '',
    basket_id: basket.id,
    supply_postcode: basket.details?.supplyAddress?.postcode,
    is_home_owner: basket.details?.occupancyType === OccupancyType.Homeowner,
    is_boiler_home_cover_available: boilerInsurance.available,
    is_income_protector_available: incomeProtector.available,
    is_hosted_link: isHostedLink,
    broadband_provisioning_supplier: networkType,
    is_property_already_supplied: basket.isPropertyAlreadySupplied ?? undefined,
    ...sessionContext,
    ...attributionContext,
  };
};

const buildTrackingContexts = (
  queryData: Nullable<GetThankYouDataQuery>,
  appointment: Nullable<Appointment>,
  userInformation: Nullable<UserInformation>,
  cbcCalcContext: Nullable<SignupCbcCalcStateEntity>,
  isHostedLink: boolean
): ExtraContext<Context>[] => {
  const { basket, attributionDetails } = queryData ?? {};

  if (!basket) {
    return [];
  }

  const contexts: ExtraContext<Context>[] = [];
  const coreServicesContext = parseCoreServicesEntity(basket);

  if (coreServicesContext) {
    contexts.push(signupCoreServicesEntity(coreServicesContext));
  }

  const applicationContext = buildApplicationContext(
    basket,
    attributionDetails ?? null,
    appointment,
    userInformation,
    isHostedLink
  );
  contexts.push(signupApplicationEntity(applicationContext));

  const accountDetails = thankYouDetailsStorage.get();
  const checkoutContext = parseCheckoutEntity(
    basket,
    attributionDetails ?? null,
    queryData?.broadbandSwitchDetails ?? null,
    accountDetails?.hasJointAccountHolder ?? false,
    accountDetails?.recentlyMovedIn ?? false
  );
  contexts.push(signupCheckoutEntity(checkoutContext));

  if (cbcCalcContext) {
    contexts.push(signupCbcCalcStateEntity(cbcCalcContext));
  }

  return contexts;
};

const useThankYouTracking = (queryData: Nullable<GetThankYouDataQuery>) => {
  const { clearCTAConversionFlag, isCTAConversionJourney } =
    useHandleCTAConversionParam();
  const trackSignupCompleted = useTrackSignupCompletedOnce();
  const userInformation = useUserInformation();
  const { appointment } = useAppointmentContext();
  const { hostedId } = useHostedAttribution();
  const { signupCbcCalcStateContext } = useTrackingContext();
  const optlyEvent = useOptlyEvent();
  const basket = queryData?.basket;
  const isHostedLink = Boolean(hostedId);

  const trackingContexts = useMemo(
    () =>
      buildTrackingContexts(
        queryData,
        appointment,
        userInformation,
        signupCbcCalcStateContext,
        isHostedLink
      ),
    [
      queryData,
      appointment,
      userInformation,
      isHostedLink,
      signupCbcCalcStateContext,
    ]
  );

  useEffect(() => {
    const basketId = basket?.id;
    const accountNumber = basket?.details?.accountNumber;

    if (!basketId || completedBasketStorage.isCompleted(basketId)) {
      return;
    }

    trackSignupCompleted(
      { account_number: accountNumber ?? '' },
      { contexts: [...trackingContexts, ...buildOptimizelyEntity()] }
    );

    const event = isCTAConversionJourney
      ? optlyEvents.engagedOrderSubmitted
      : optlyEvents.orderSubmitted;
    optlyEvent(event);

    if (isCTAConversionJourney) {
      clearCTAConversionFlag();
    }

    completedBasketStorage.setCompleted(basketId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export { useThankYouTracking };
